import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import authApi from '../api/authApi';
import { STORAGE_TOKEN_KEY, STORAGE_USER_KEY } from '../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const persistSession = (token, userData) => {
    localStorage.setItem(STORAGE_TOKEN_KEY, token);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(userData));
    setUser(userData);
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
    setUser(null);
  }, []);

  // Al cargar la app: si hay token guardado, valida la sesion contra el backend
  useEffect(() => {
    const initialize = async () => {
      const token = localStorage.getItem(STORAGE_TOKEN_KEY);

      if (!token) {
        setIsInitialized(true);
        return;
      }

      try {
        const response = await authApi.me();
        const userData = response.data?.data || response.data;
        setUser(userData);
        localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(userData));
      } catch (error) {
        clearSession();
      } finally {
        setIsInitialized(true);
      }
    };

    initialize();
  }, [clearSession]);

  // Escucha el evento disparado por el interceptor de axios ante un 401
  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('pc:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('pc:unauthorized', handleUnauthorized);
  }, []);

  const login = useCallback(async (credentials) => {
    const response = await authApi.login(credentials);
    const { token, user: userData } = response.data?.data || response.data;
    persistSession(token, userData);
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const response = await authApi.register(data);
    const { token, user: userData } = response.data?.data || response.data;
    persistSession(token, userData);
    return userData;
  }, []);

  // Refresca el usuario en cache (contexto + localStorage) tras editar el propio perfil,
  // sin necesitar un nuevo login.
  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(userData));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Si el backend ya invalido el token o no responde, igual limpiamos localmente
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      role: user?.role,
      isAuthenticated: Boolean(user),
      isInitialized,
      login,
      register,
      logout,
      updateUser,
    }),
    [user, isInitialized, login, register, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export default AuthContext;
